import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';

import {
  buildDefinitionFromStore,
  selectConceptGroups,
  selectHasUnresolvedStubs,
  selectIsEditable,
  selectIsOwner,
  selectLatestStatus,
  useCohortDefinitionStore,
} from '@verantos-dev/app-state-cohort-definition';
import { useIsWaitingForResponse } from '@verantos-dev/app-state-research-assistant';
import { CohortDefinitionStatus } from '@verantos-dev/types-cohorts';

import { useCohortMetadata } from '../../../../hooks/common/useCohortMetadata';
import { useEmitResearchAssistantUpdate } from '../../../../hooks/common/useEmitResearchAssistantUpdate';
import type { CriterionRow } from '../useCriteriaSection';

/**
 * Handlers for a single criterion card in the gen-3 cohort detail
 * Definition tab. Pure Zustand — no Redux, no RTK.
 *
 * - **Delete** dispatches the existing Zustand action
 *   (`deleteAnalyticalVariableCriterion` or `deleteIndexDateCriterion`).
 *   Both already exist on the store; gen-3 doesn't introduce new
 *   mutators here. The affordance exists only for the cohort owner
 *   (`isOwner`); it renders disabled while the cohort is locked
 *   (processing / final / agent working / AD-referenced).
 * - There is deliberately **no edit affordance** — inline AV editing
 *   from the cohort card is not a platform capability; changes go
 *   through the Research Assistant.
 */
export const useCriterionCard = ({ criterion }: CriterionRow) => {
  const intl = useIntl();

  const latestStatus = useCohortDefinitionStore(selectLatestStatus);
  const isEditable = useCohortDefinitionStore(selectIsEditable);
  const isOwner = useCohortDefinitionStore(selectIsOwner);
  const conceptGroups = useCohortDefinitionStore(selectConceptGroups);
  const deleteAvCriterion = useCohortDefinitionStore((s) => s.deleteAnalyticalVariableCriterion);
  const deleteIndexDateCriterion = useCohortDefinitionStore((s) => s.deleteIndexDateCriterion);
  const { cohortOwner, cohortLastModified } = useCohortMetadata();

  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);

  const deleteLibraryRefCriterion = useCohortDefinitionStore(
    (s) => s.deleteLibraryReferenceCriterion
  );

  const emitResearchAssistantUpdate = useEmitResearchAssistantUpdate();

  // OR-6078: single-writer lock. The Definition tab is editable only when the
  // user owns the cohort — i.e. the agent is NOT mid-operation on it. The
  // agent owns it whenever a turn is in flight or it's parked on a checkpoint
  // (`isWaitingForRA`, fed by the gen-3 run state), the cohort is still being
  // assembled (`hasUnresolvedStubs` — pending stubs the resolve loop will
  // bind), or it's in a terminal state (PROCESSING / FINAL). Editing in any of
  // those windows would race the agent or contradict a plan awaiting approval.
  const isWaitingForRA = useIsWaitingForResponse();
  const hasUnresolvedStubs = useCohortDefinitionStore(selectHasUnresolvedStubs);
  const isProcessing = latestStatus === CohortDefinitionStatus.PROCESSING;
  const isFinal = latestStatus === CohortDefinitionStatus.FINAL;
  // Owner whose cohort is referenced by an analytical dataset: the delete
  // affordance stays visible but locked, same as the other lock states.
  const isAdLocked = isOwner && !isEditable;
  const actionsDisabled =
    isProcessing || isFinal || isWaitingForRA || hasUnresolvedStubs || isAdLocked;
  const actionsDisabledReason = isWaitingForRA
    ? intl.formatMessage({ id: 'projects.cohortBuilderPage.criteriaSection.lock.agentWorking' })
    : hasUnresolvedStubs
    ? intl.formatMessage({ id: 'projects.cohortBuilderPage.criteriaSection.lock.assembling' })
    : isProcessing
    ? intl.formatMessage({ id: 'projects.cohortBuilderPage.criteriaSection.lock.processing' })
    : isFinal
    ? intl.formatMessage({ id: 'projects.cohortBuilderPage.criteriaSection.lock.finalized' })
    : isAdLocked
    ? intl.formatMessage({ id: 'projects.cohortBuilderPage.criteriaSection.lock.adLocked' })
    : undefined;
  const canDelete = isOwner && !actionsDisabled;

  const handleDelete = useCallback(() => {
    if (criterion.criteriaType === 'index_date') {
      deleteIndexDateCriterion();
    } else if (criterion.criteriaType === 'analytical_variable') {
      if (criterion.analyticalVariableId !== null) {
        // Legacy clone-on-add: criterion + local AV both need cleanup.
        deleteAvCriterion(criterion.analyticalVariableId);
      } else if (criterion.sourceLibraryAvId) {
        // Library-reference (PR #3647): only the criterion is local.
        deleteLibraryRefCriterion(criterion.sourceLibraryAvId);
      } else {
        // Unbound stub: not user-deletable — the single-writer lock keeps the
        // card read-only while the cohort still has stubs (see actionsDisabled).
        return;
      }
    } else {
      return;
    }
    // Persist the removal. The Zustand delete actions above mutate the store
    // synchronously, so `buildDefinitionFromStore` already reflects the removal.
    // Without this autosave the delete lived only in memory and reappeared on
    // navigate-away-and-back, since the draft was never updated (OR-6110).
    emitResearchAssistantUpdate(buildDefinitionFromStore());
  }, [
    criterion,
    deleteAvCriterion,
    deleteIndexDateCriterion,
    deleteLibraryRefCriterion,
    emitResearchAssistantUpdate,
  ]);

  const handlePreview = useCallback(() => {
    setIsPreviewModalOpen(true);
  }, []);

  const handleClosePreviewModal = useCallback(() => {
    setIsPreviewModalOpen(false);
  }, []);

  return {
    isOwner,
    canDelete,
    actionsDisabled,
    actionsDisabledReason,
    handleDelete,
    isPreviewModalOpen,
    handlePreview,
    handleClosePreviewModal,
    conceptGroups,
    cohortOwner,
    cohortLastModified,
  };
};
